import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {
  SWITCH_NUMBER_STRING,
  SWITCH_NUMBER_OP,
  SWITCH_STATE_OP,
} from "lookup/GlobalConstants";
import {
  SWIM_STATES_FOR_SELECT,
  DEFAULT_SWIM_SORT,
  SWIM_FILTER,
  DEFAULT_FILTER,
} from "lookup/SwimConstants";
import { showNotification } from "services/NotificationService";
import ServerSideReactTable from "commons/table/ServerSideReactTable";
import { createColumnListForSwitchList } from "./CreateColumnListForSwitchList";

const Tip = (props) => (
  <div style={{ textAlign: "center", color: "#888", fontSize: "0.9em" }}>
    <em>{props.children}</em>
  </div>
);

const Resize = (col) => (
  <div style={{ textAlign: "center", fontSize: "0.9em" }}>{col}</div>
);

const DEFAULT_PAGE_SIZE = 15;

// updateData
// selectable
const SwitchListCommon = (props) => {
  const {
    columnList,
    isSortFilterEnabledForCust,
    isInitialLoadComplete,
    listState,
    isError,
    resetError,
    updateData: externalUpdateData,
    updateInterventionList,
    data,
    pages,
    loading,
    totalElements,
    isViewable,
    updateIndexOnList,
    toggleSelection,
    isSelectAll,
    selected,
    resetTableSelection,
    isShowBioSimilarToggle,
    actionType,
  } = props;

  const [internalListState, setInternalListState] = useState({});
  const navigate = useNavigate();
  // Track last dispatched request to avoid duplicate backend calls with same params
  const lastRequestKeyRef = useRef(null);

  const computedColumnList = useMemo(
    () => createColumnListForSwitchList(columnList, isSortFilterEnabledForCust, actionType),
    [columnList, isSortFilterEnabledForCust, actionType]
  );

  const effectiveListState = listState || internalListState;

  const updateData = useCallback(
    (listOptions, preventUpdate) => {
      if (!preventUpdate) {
        listOptions.filtered = listOptions.filtered.filter((element) => {
          if (
            typeof element.value === "string" ||
            Array.isArray(element.value)
          ) {
            return element.value.length > 0;
          }
          if (typeof element.value === "boolean") {
            return true;
          }
          return element.value !== undefined && element.value !== null;
        });

        // Ensure default sorting is respected
        if (listOptions.sorted !== DEFAULT_SWIM_SORT) {
          listOptions.sorted = listOptions.sorted.map((element) => ({
            ...element,
            isDefault: false,
          }));
        }

        // Normalize switch number filter for backend
        const indexSwitch = listOptions.filtered.findIndex(
          (element) => element.id === "switchNumber"
        );
        if (indexSwitch !== -1) {
          listOptions.filtered[indexSwitch] = {
            ...listOptions.filtered[indexSwitch],
            operator: SWITCH_NUMBER_OP,
            id: SWITCH_NUMBER_STRING,
          };
        }

        // Handle default state filter
        let filtered = listOptions.filtered.filter(
          (element) =>
            !(element.id === "state" && element.value === DEFAULT_FILTER)
        );

        if (filtered.length !== listOptions.filtered.length) {
          filtered = [...filtered, SWIM_FILTER.defualtFrontEnd];
        }

        const indexState = filtered.findIndex(
          (element) => element.id === "state"
        );
        if (indexState !== -1) {
          const stateFilter = filtered[indexState];
          const rawValues = stateFilter.value || [];
          const nextValues = rawValues.map((value) =>
            typeof value === "string" ? value : value.name
          );

          if (nextValues.includes("DEFAULT_FILTERS")) {
            filtered[indexState] = SWIM_FILTER.defualtFrontEnd;
          } else {
            filtered[indexState] = {
              ...stateFilter,
              value: nextValues,
            };
          }
        }

        // Bio Similar frontend filters
        if (isShowBioSimilarToggle) {
          const clientNameFilterExists = filtered.some(
            (f) => f.id === "clientNameAbbreviation"
          );
          const customerNameFilterExists = filtered.some(
            (f) => f.id === "customerNameAbbreviation"
          );

          if (!clientNameFilterExists) {
            filtered.push({
              id: "clientNameAbbreviation",
              value: "WMT",
            });
          }

          if (!customerNameFilterExists) {
            filtered.push({
              id: "customerNameAbbreviation",
              value: "WMT",
            });
          }
        }

        const requestKey = JSON.stringify({
          page: listOptions.page,
          pageSize: listOptions.pageSize,
          sorted: listOptions.sorted,
          filtered,
        });
        if (lastRequestKeyRef.current === requestKey) {
          return;
        }
        lastRequestKeyRef.current = requestKey;

        if (externalUpdateData) {
          externalUpdateData({ ...listOptions, filtered });
        } else if (updateInterventionList) {
          updateInterventionList(
            listOptions.pageSize,
            listOptions.page,
            listOptions.sorted,
            filtered,
            listOptions.resized,
            listOptions.expanded
          );
        }
      } else {
        setInternalListState(listOptions);
      }
    },
    [externalUpdateData, updateInterventionList, isShowBioSimilarToggle]
  );

  useEffect(() => {
    if (isInitialLoadComplete) {
      updateData(listState);
    }
  }, [isInitialLoadComplete]);

  useEffect(() => {
    if (isError) {
      showNotification({
        title: "Error Loading Data!",
        message: "You have entered invalid information.",
        position: "bl",
        type: "error",
      });
      if (resetError) {
        resetError();
    }
  }
  }, [isError, resetError]);

  const patchedListState = useMemo(() => {
    const ls = { ...effectiveListState };
    const idx = ls.filtered.findIndex(
      (element) => element.id === SWITCH_NUMBER_STRING
    );
    if (idx !== -1) {
      ls.filtered = [...ls.filtered];
      ls.filtered[idx] = { ...ls.filtered[idx], id: "switchNumber" };
    }
    return ls;
  }, [effectiveListState]);

  const onUpdateTable = useCallback(
    (filterValues, sortBy, pageSize, pageNo) => {
      const nextState = {
        page: pageNo,
        pageSize: pageSize,
        sorted: sortBy || [],
        filtered: filterValues,
        resized: [],
        expanded: {},
      };
      updateData(nextState);
    },
    [updateData]
    );

    return (
      <Row>
        <Col md={12}>
          <ServerSideReactTable
          columnDefination={computedColumnList}
            data={data}
          currentPage={patchedListState.page}
          sorted={patchedListState.sorted}
          filters={patchedListState.filtered}
            totalPages={pages}
            totalRecords={totalElements == "loading" ? 0 : totalElements}
            loading={loading}
            overrideRowId={"wfItemId"}
            pageSizeOptions={[5, 10, 15, 20, 30, 50]}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            onUpdateTableData={onUpdateTable}
            onRowClick={(row) => {
            if (row && updateIndexOnList) {
              updateIndexOnList(row.index);
              navigate(`/switch/${row.original.wfItemId}`);
              }
            }}
          enableMultiRowSelection={!!toggleSelection}
            addRowSelection={
              isViewable !== undefined && isViewable === true
                ? false
              : !!toggleSelection
            }
          onRowSelect={toggleSelection}
          toggleAllRowsSelect={isSelectAll}
          globalSelected={selected ? selected : []}
          resetTableSelection={resetTableSelection || null}
          />
          <Tip>
            Tip: Hold shift when sorting to multi-sort and disable sort!
          </Tip>
        </Col>
      </Row>
    );
};

export default SwitchListCommon;
